// Admin API Service
// Handles communication with the backend API for admin endpoints

import { authenticatedFetch } from './authHelper';

/**
 * Admin API Service for admin dashboard functionality
 * This service handles API calls to admin endpoints with proper authentication
 */
class AdminApiService {
  constructor() {
    // Use environment-specific backend URL or fallback to relative URLs for development
    this.baseUrl = '/api/admin';
    this.timeout = 30000; // 30 seconds
  }

  /**
   * Make authenticated API request to admin endpoints
   * @param {string} endpoint - API endpoint path
   * @param {Object} options - Fetch options
   * @returns {Promise<Response>} API response
   */
  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    console.log(`[AdminApi] Making request to: ${url}`);
    
    const response = await authenticatedFetch(url, options);
    
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    if (response.status === 403) {
      throw new Error('Admin access required. You do not have permission.');
    }
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response received:', text);
      throw new Error('Invalid response format from server');
    }

    return response;
  }

  /**
   * Get analytics data
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics() {
    const response = await this.makeRequest('/analytics');
    return response.json();
  }

  /**
   * Get beta users data
   * @returns {Promise<Object>} Beta users data
   */
  async getBetaUsers() {
    const response = await this.makeRequest('/beta-users');
    return response.json();
  }

  /**
   * Get users data with pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Users data
   */
  async getUsers(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/users?${queryString}` : '/users';
    const response = await this.makeRequest(endpoint);
    return response.json();
  }

  /**
   * Get user details by ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User details
   */
  async getUserDetails(userId) {
    const response = await this.makeRequest(`/user/${userId}`);
    return response.json();
  }

  /**
   * Add a new beta user
   * @param {Object} userData - User data
   * @returns {Promise<Object>} API response
   */
  async addBetaUser(userData) {
    const response = await this.makeRequest('/beta-users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    return response.json();
  }

  /**
   * Update beta user status
   * @param {string} userId - User ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} API response
   */
  async updateBetaUser(userId, updateData) {
    const response = await this.makeRequest(`/beta-users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    return response.json();
  }

  /**
   * Get admin stats
   * @returns {Promise<Object>} Admin stats
   */
  async getStats() {
    const response = await this.makeRequest('/stats');
    return response.json();
  }
}

// Create singleton instance
const adminApiService = new AdminApiService();

// Export for use in components
export default adminApiService;

// Export individual functions for convenience
export const getAnalytics = () => adminApiService.getAnalytics();
export const getBetaUsers = () => adminApiService.getBetaUsers();
export const getUsers = (params) => adminApiService.getUsers(params);
export const getUserDetails = (userId) => adminApiService.getUserDetails(userId);
export const addBetaUser = (userData) => adminApiService.addBetaUser(userData);
export const updateBetaUser = (userId, updateData) => adminApiService.updateBetaUser(userId, updateData);
export const getStats = () => adminApiService.getStats();
